import { memo, useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import { Box, IconButton, Typography } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import {
  isModalOpen,
  updateCartItemCount,
  usePersistentCartItems,
} from 'store/cartItems';
import { productType } from '@utils/types';

import theme from 'src/style-system/theme';
import { GetFilledChilli } from '@components/FilledChilli';
import { GetEmptyChilli } from '@components/EmptyChilli';
import ArrowTooltips from '@components/Tooltip';
import { GetVegan } from '@components/Vegan';
import Modal from '@components/DishModal';

import { useStyles } from './useStyles';

interface DishDisplayProps {
  product: productType;
  quantity?: number;
  valueRecommended?: number;
  isSuggestion?: boolean;
  isInfoDisplay?: boolean;
  mode?: string;
  elevarAddToCartEvent?: any;
  elevarRemoveFromCartEvent?: any;
  elevarViewItemEvent?: any;
}

const NewDishDisplay = ({
  product,
  quantity,
  valueRecommended,
  isSuggestion = false,
  isInfoDisplay = false,
  mode,
  elevarAddToCartEvent,
  elevarRemoveFromCartEvent,
  elevarViewItemEvent,
}: DishDisplayProps) => {
  const [open, setOpen] = useState<boolean>(false);

  const classes = useStyles();

  const cartItems = usePersistentCartItems();

  const handleOpen = (productTitle: string) => {
    setOpen(true);

    //@ts-ignore
    window?.dataLayer.push({
      text: productTitle,
      event: 'item_open',
    });
  };
  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (isInfoDisplay) {
      if (open) {
        isModalOpen(true);
      } else {
        isModalOpen(false);
      }
    }
  }, [isInfoDisplay, open]);

  const decrement = (productId: number, productTitle: string) => {
    // for GA event
    if (cartItems[productId] === 1) {
      //@ts-ignore
      window?.dataLayer.push({
        text: productTitle,
        event: isSuggestion ? 'side_remove' : 'item_remove',
      });
    }

    if (!mode && elevarRemoveFromCartEvent) {
      //for elvart remove from cart event
      elevarRemoveFromCartEvent(product);
    }

    if (cartItems[productId]) {
      updateCartItemCount(productId, cartItems[productId] - 1);
    }
  };
  const increment = (productId: number, productTitle: string) => {
    if (!mode && elevarAddToCartEvent) {
      //fire elevar add to cart event
      elevarAddToCartEvent(product);
    }

    // for GA event
    //@ts-ignore
    window?.dataLayer.push({
      text: productTitle,
      event: isSuggestion ? 'side_add' : 'item_add',
    });

    if (cartItems[productId]) {
      updateCartItemCount(productId, cartItems[productId] + 1);
    } else {
      updateCartItemCount(productId, 1);
    }
  };

  const isDecIconButtonDisabled = useMemo(() => {
    return !cartItems[product?.id] || cartItems[product?.id] === 0;
  }, [cartItems]);

  const getMealCount = useMemo(() => {
    return cartItems[product?.id] || 0;
  }, [cartItems]);

  return (
    <>
      <Box className={classes.mainContainer}>
        <Box className={classes.imgTitleWrapper}>
          <Box
            position="relative"
            width={{
              xs: '75px',
            }}
            height={{
              xs: '75px',
            }}
            sx={{ cursor: 'pointer' }}
          >
            {product?.image_url ? (
              <Image
                src={product?.image_url}
                alt={product?.title}
                objectFit="contain"
                layout="fill"
              />
            ) : null}
          </Box>
          <Box>
            <Box
              sx={{ display: 'flex', alignItems: 'center', columnGap: '5px' }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography
                  variant="body2"
                  align="left"
                  color="garamMasala.main"
                  mb="5px"
                >
                  {product?.title}
                </Typography>
              </Box>
              {isInfoDisplay ? (
                <InfoOutlinedIcon
                  sx={{ cursor: 'pointer', color: 'cinnamon.main' }}
                  onClick={() => handleOpen(product?.title)}
                />
              ) : null}
            </Box>

            {isSuggestion ? (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    columnGap: '4px',
                  }}
                >
                  {product?.vegan ? (
                    <ArrowTooltips title="Vegan">
                      <GetVegan />
                    </ArrowTooltips>
                  ) : null}
                  {product?.spice_level > 0 ? (
                    <ArrowTooltips title="Spice Level">
                      <>
                        {product?.spice_level >= 1 ? (
                          <GetFilledChilli />
                        ) : (
                          <GetEmptyChilli />
                        )}
                        {product?.spice_level >= 2 ? (
                          <GetFilledChilli />
                        ) : (
                          <GetEmptyChilli />
                        )}
                        {product?.spice_level >= 3 ? (
                          <GetFilledChilli />
                        ) : (
                          <GetEmptyChilli />
                        )}
                        {product?.spice_level >= 4 ? (
                          <GetFilledChilli />
                        ) : (
                          <GetEmptyChilli />
                        )}
                        {product?.spice_level >= 5 ? (
                          <GetFilledChilli />
                        ) : (
                          <GetEmptyChilli />
                        )}
                      </>
                    </ArrowTooltips>
                  ) : null}
                </Box>
                <Typography variant="body2" align="left" color="cumin.main">
                  {'Recommendation: ' + valueRecommended}
                </Typography>
              </>
            ) : null}
          </Box>
        </Box>

        <Box className={classes.btnContainer}>
          <IconButton
            onClick={() => decrement(product?.id, product?.title)}
            size="large"
            disabled={isDecIconButtonDisabled}
            sx={{
              padding: '8px',
              [theme.breakpoints.down(420)]: {
                padding: '6px',
              },
            }}
          >
            <RemoveCircleIcon
              fontSize="inherit"
              sx={{
                color: !isDecIconButtonDisabled
                  ? 'cinnamon.main'
                  : 'lightGray.main',
              }}
            />
          </IconButton>

          <Typography variant="body2" color="cinnamon.main">
            {quantity !== null && quantity !== undefined
              ? quantity
              : getMealCount}
          </Typography>

          <IconButton
            onClick={() => increment(product?.id, product?.title)}
            size="large"
            sx={{
              padding: '8px',
              [theme.breakpoints.down(420)]: {
                padding: '6px',
              },
            }}
          >
            <AddCircleIcon
              sx={{
                color: 'cinnamon.main',
              }}
              fontSize="inherit"
            />
          </IconButton>
        </Box>
      </Box>
      {open ? (
        <Modal
          open={open}
          handleClose={handleClose}
          product={product}
          mode={mode}
          elevarAddToCartEvent={elevarAddToCartEvent}
          elevarRemoveFromCartEvent={elevarRemoveFromCartEvent}
          elevarViewItemEvent={elevarViewItemEvent}
        />
      ) : null}
    </>
  );
};

export default memo(NewDishDisplay);
